import { useEffect, useRef } from "react";
import { useCreatePageStore } from "@/stores/createPageStore";
import { resumePendingSubmittedMessageOnce } from "@/utils/pendingSubmittedMessageClaim";

/**
 * Replays only a plain customer message that was already submitted before an
 * auth gate. Capability-bearing preset, render, modification, quote, and
 * purchase actions are never recorded here and therefore never auto-run.
 */
export function usePendingSubmittedMessageResume({
  authLoading,
  hasAccount,
  restorationPending,
  creditsLoading,
  hasCredits,
  onResume,
}: {
  authLoading: boolean;
  hasAccount: boolean;
  restorationPending: boolean;
  creditsLoading: boolean;
  hasCredits: boolean;
  onResume: (message: string) => void | Promise<void>;
}): void {
  const pendingMessage = useCreatePageStore(
    state => state.agent.pendingSubmittedMessage
  );
  const pendingMessageId = useCreatePageStore(
    state => state.agent.pendingSubmittedMessageId
  );
  const startedRef = useRef(false);

  useEffect(() => {
    if (
      startedRef.current ||
      authLoading ||
      !hasAccount ||
      restorationPending ||
      !pendingMessage ||
      !pendingMessageId ||
      typeof window === "undefined"
    ) {
      return;
    }

    const url = new URL(window.location.href);
    // Let an exact quote or pack continuation finish first. Those actions are
    // separately capability-bound and must never be dispatched by this hook.
    if (
      url.searchParams.get("quote_intent") === "1" ||
      url.searchParams.get("credit_intent") === "1"
    ) {
      return;
    }
    // A URL marker is only a navigation hint and may be consumed by another
    // recovery effect before restoration settles. The account balance is the
    // durable authority: never bypass the chat wall until credits are known and
    // positive.
    if (creditsLoading || !hasCredits) {
      return;
    }

    startedRef.current = true;
    const message = pendingMessage.trim();
    const clearPendingMessageIfExact = () => {
      const latestStore = useCreatePageStore.getState();
      if (latestStore.agent.pendingSubmittedMessageId !== pendingMessageId) {
        return false;
      }
      latestStore.setPendingSubmittedMessage(null);
      return true;
    };
    const clearCreditGateHint = () => {
      const latestUrl = new URL(window.location.href);
      latestUrl.searchParams.delete("credit_gate");
      window.history.replaceState(
        {},
        "",
        latestUrl.pathname + latestUrl.search + latestUrl.hash
      );
    };
    void (async () => {
      const outcome = await resumePendingSubmittedMessageOnce(
        pendingMessageId,
        async () => {
          // Keep the exact pending marker through the request. If the tab dies
          // before the response completes, the restored state plus the
          // in-flight claim lease can safely recover it on the next page load.
          await onResume(message);
        }
      );

      if (outcome === "completed") {
        // A 402 creates a new pending id. Clear only the id this callback
        // actually delivered so the fresh credit-gated attempt survives.
        const callbackAlreadyConsumedPending =
          useCreatePageStore.getState().agent.pendingSubmittedMessageId ===
          null;
        if (clearPendingMessageIfExact() || callbackAlreadyConsumedPending) {
          clearCreditGateHint();
        }
      }

      // Another tab already owns this exact submitted turn. Clear only this
      // tab's restored copy so it cannot keep reopening the credit wall.
      if (outcome === "already_consumed") {
        if (clearPendingMessageIfExact()) {
          clearCreditGateHint();
        }
      } else if (outcome === "unavailable" || outcome === "action_failed") {
        // If neither atomic browser primitive is usable—or the callback itself
        // fails after the claim—fall back to an explicit manual retry. This is
        // visible and safe: the exact words return to the composer, but no
        // second network action starts automatically.
        if (clearPendingMessageIfExact()) {
          clearCreditGateHint();
          const store = useCreatePageStore.getState();
          store.setPendingComposerSeed(message);
          store.setAgentError(
            "Your message was restored, but we couldn't send it. Select Send below to try again."
          );
        }
      }
    })();
  }, [
    authLoading,
    creditsLoading,
    hasAccount,
    hasCredits,
    onResume,
    pendingMessage,
    pendingMessageId,
    restorationPending,
  ]);
}
